import { useMemo, useRef, useEffect } from 'react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import { Checkbox } from '../../../../Form/Checkbox/index.tsx';
import type { Option, FormatOptionLabel } from '../../types.ts';
import { Label, OptionWrapper } from './styled.ts';

interface OptionProps<T> {
  option: Option<T>;
  checked: boolean;
  isFocused?: boolean;
  onClick?: () => void;
  formatOptionLabel?: FormatOptionLabel<T>;
}

export const OptionCheckbox = memoGeneric(function OptionCheckbox<T>({
  option,
  checked,
  isFocused = false,
  onClick,
  formatOptionLabel,
}: OptionProps<T>) {
  const label = useMemo(() => (formatOptionLabel ? formatOptionLabel(option) : option.label), [formatOptionLabel, option]);
  const ref = useRef<HTMLDivElement>(null);

  // Scroll into view when focused
  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  return (
    <OptionWrapper onClick={onClick} $isFocused={isFocused} ref={ref} data-testid='option-checkbox'>
      <Checkbox checked={checked} onChange={onClick} />
      <Label data-testid='option-label'>{label}</Label>
    </OptionWrapper>
  );
});
